/**
 * Build-time configuration.
 *
 * Vite substitutes these at build time via `define` in vite.config.ts, so the
 * values are frozen into the bundle. Changing one requires a rebuild, not a
 * restart. That is a property of any static SPA and is worth stating plainly:
 * there is no runtime configuration channel here.
 *
 * Every value is validated on module load rather than at first use. A missing
 * API base URL should fail while someone is looking at the deploy, not later
 * when a user presses a button.
 *
 * `appOrigin` below is the exception and is not build-time configuration at
 * all. It is here because it is what callers reach for when they would
 * otherwise reach for a configured base URL, and putting it anywhere else is
 * how the configured one gets reintroduced.
 */

function required(name: string, value: string | undefined): string {
    if (!value) {
        throw new Error(
            `${name} is not set. It is baked in at build time, so set it in ` +
                `the build environment and rebuild.`
        );
    }
    return value;
}

/** Trailing slash guaranteed, so callers can join paths without guessing. */
function normaliseBase(url: string): string {
    return url.endsWith("/") ? url : `${url}/`;
}

/**
 * The origin this app is currently being served from, with no trailing slash.
 *
 * Read from the browser rather than baked in, because it is the one value a
 * build cannot know. The site answers on more than one hostname, and a constant
 * is wrong on every host except the one it names: REACT_APP_BASEURL said
 * hytchbuddy.techbeaver.io while the site also serves hytchbuddy.com, so a
 * rider who paid on the custom domain would have been returned to the other
 * origin, where none of their browser state exists.
 *
 * Anything a payment provider or an OAuth provider redirects back to belongs
 * here, not in the block below.
 */
export function appOrigin(): string {
    return window.location.origin;
}

export const env = {
    /** Root of the Go API, including the /api/v1 prefix. */
    apiBaseUrl: normaliseBase(
        required("REACT_APP_API_BASEURL", process.env.REACT_APP_API_BASEURL)
    ),

    environment: process.env.REACT_APP_ENVIRONMENT ?? "development",

    /**
     * Federated sign-in client ids. Public values, and empty when unset.
     *
     * These are optional on purpose, unlike the two URLs above. A missing API
     * base URL breaks every screen, so the build refuses it; a missing client
     * id disables one button. `socialSignIn` below turns the buttons off rather
     * than rendering one that cannot work, which is the difference between a
     * deployment without Apple configured and a deployment where Apple is
     * broken.
     *
     * A client id is not a secret. It is sent to the provider in the clear on
     * every sign-in and is visible in the page source either way, so it lives
     * in the committed .env.production alongside the Maps browser key.
     */
    googleOAuthClientId: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID ?? "",

    /**
     * Apple's Services ID, not the iOS bundle id. The native clients
     * authenticate as com.hytchbuddy.ios; the web is a separate audience and
     * the API accepts both.
     */
    appleOAuthClientId: process.env.REACT_APP_APPLE_OAUTH_CLIENT_ID ?? "",

    /**
     * Must be a URI registered against the Services ID in Apple's console, and
     * must be the one sent when the authorization code is redeemed. Apple
     * compares the two and refuses a mismatch, popup mode included, so a wrong
     * value here fails at redemption rather than at sign-in and shows up as an
     * account that can be created but never revoked.
     */
    appleRedirectUri: process.env.REACT_APP_APPLE_REDIRECT_URI ?? "",
} as const;

/** Which federated providers this build is configured for. */
export const socialSignIn = {
    google: env.googleOAuthClientId !== "",
    // Apple needs both: a Services ID to identify the client and a registered
    // redirect URI to redeem the code against. With only one of them the button
    // would render and then fail, so it stays hidden.
    apple: env.appleOAuthClientId !== "" && env.appleRedirectUri !== "",
} as const;

// There is deliberately no googleMapsApiKey here. The key is only ever needed
// by the Maps script tag in index.html, which is HTML and so out of reach of
// `define`; vite.config.ts substitutes it there instead. A field on this object
// was read by nothing, and its comment promised a degraded map that was never
// built. Google renders an error panel over the map when the key is missing, it
// does not fall back, so the build requires the key rather than pretending.

export const isProduction = env.environment === "production";

/** Just the values `unconfiguredProviders` needs, so it can be tested. */
export interface FederatedConfig {
    googleOAuthClientId: string;
    appleOAuthClientId: string;
    appleRedirectUri: string;
}

/**
 * The federated providers this build cannot offer, and the variables to set.
 *
 * Hiding a button nobody can use is right for the visitor and invisible to
 * whoever deployed it: a build with no Apple credentials and a build that
 * deliberately does not offer Apple render identically. The mobile client had
 * the same shape and shipped every Android build with no Maps key for months,
 * because a missing key and an intentionally map-less screen drew the same
 * placeholder.
 *
 * So the degraded state is announced at start-up instead. This returns the list
 * rather than logging it, because what is worth testing is which variable gets
 * named, not whether something reached the console.
 */
export function unconfiguredProviders(config: FederatedConfig = env): string[] {
    const missing: string[] = [];

    if (config.googleOAuthClientId === "") {
        missing.push("Google (REACT_APP_GOOGLE_OAUTH_CLIENT_ID)");
    }

    // Apple needs both, and naming only the one that is absent is the
    // difference between a two-minute fix and an afternoon.
    const appleMissing = [
        config.appleOAuthClientId === "" && "REACT_APP_APPLE_OAUTH_CLIENT_ID",
        config.appleRedirectUri === "" && "REACT_APP_APPLE_REDIRECT_URI",
    ].filter((name): name is string => typeof name === "string");

    if (appleMissing.length > 0) {
        missing.push(`Apple (${appleMissing.join(", ")})`);
    }

    return missing;
}
